import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";

export const OPTION_LABELS = {
  body_region: "Body region",
  injury_area: "Area of injury",
  condition: "Condition / diagnosis",
  category: "Category",
};

export function OptionSelect({ kind, value = "", onChange, options = {} }) {
  const rows = options[kind] || [];
  const present = rows.some((row) => row.name === value);
  return (
    <select
      aria-label={OPTION_LABELS[kind]}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-900"
    >
      <option value="">{`No ${OPTION_LABELS[kind].toLowerCase()}`}</option>
      {value && !present && <option value={value}>{`${value} (existing)`}</option>}
      {rows.filter((row) => row.is_active || row.name === value).map((row) => (
        <option key={row.key} value={row.name}>{`${row.name}${row.is_active ? "" : " (archived)"}`}</option>
      ))}
    </select>
  );
}

export function ExerciseOptionManager({ open, onOpenChange, options, reload }) {
  const [kind, setKind] = useState("condition");
  const [newName, setNewName] = useState("");
  const [editingKey, setEditingKey] = useState("");
  const [editingName, setEditingName] = useState("");
  const [busy, setBusy] = useState(false);
  const rows = options[kind] || [];

  const run = async (request, success) => {
    setBusy(true);
    try {
      await request();
      await reload();
      toast.success(success);
      setNewName("");
      setEditingKey("");
    } catch (error) {
      toast.error(formatApiError(error.response?.data?.detail) || error.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-h-[90vh] max-w-xl overflow-y-auto rounded-[32px]">
        <DialogHeader><DialogTitle>Manage Exercise Options</DialogTitle><DialogDescription>Choose a body region, injury area, condition or category to add, rename, archive or remove. In-use options can be renamed or archived without removing exercises.</DialogDescription></DialogHeader>
        <div className="space-y-4 p-4">
          <select aria-label="Option type" value={kind} onChange={(event) => { setKind(event.target.value); setEditingKey(""); }} className="h-11 w-full rounded-xl border border-slate-200 px-3 text-sm">
            {Object.entries(OPTION_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <div className="flex gap-2">
            <Input aria-label={`New ${OPTION_LABELS[kind]}`} placeholder={`New ${OPTION_LABELS[kind].toLowerCase()}`} value={newName} onChange={(event) => setNewName(event.target.value)} />
            <Button disabled={busy || !newName.trim()} onClick={() => run(() => api.post("/admin/exercise-options", { kind, name: newName }), "Option added")}>Add</Button>
          </div>
          <div className="space-y-2">
            {rows.map((row) => (
              <div key={row.key} className="rounded-xl border border-slate-200 p-3">
                <div className="flex items-center justify-between gap-2"><span className="text-sm font-semibold">{row.name}{row.is_active ? "" : " (archived)"}</span><span className="text-xs text-slate-500">{row.usage_count} in use</span></div>
                {editingKey === row.key && <div className="mt-2 flex gap-2"><Input aria-label="Rename option" value={editingName} onChange={(event) => setEditingName(event.target.value)} /><Button disabled={busy || !editingName.trim()} size="sm" onClick={() => run(() => api.put(`/admin/exercise-options/${kind}/${row.id}`, { name: editingName }), "Option renamed")}>Save</Button></div>}
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => { setEditingKey(row.key); setEditingName(row.name); }}>Rename</Button>
                  {row.is_active
                    ? <Button variant="outline" size="sm" disabled={busy} onClick={() => run(() => api.post(`/admin/exercise-options/${kind}/${row.id}/archive`), "Option archived")}>Archive</Button>
                    : <Button variant="outline" size="sm" disabled={busy} onClick={() => run(() => api.post(`/admin/exercise-options/${kind}/${row.id}/restore`), "Option restored")}>Restore</Button>}
                  {!row.usage_count && <Button variant="destructive" size="sm" disabled={busy} onClick={() => window.confirm(`Remove ${row.name}?`) && run(() => api.delete(`/admin/exercise-options/${kind}/${row.id}`), "Option removed")}>Remove</Button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
